<template>
  <Teleport to="body">
    <BaseModal 
      v-model="isOpen" 
      :title="tg('languageSwitcher', 'allLanguages')"
      v-if="isOpen"
      modalName="language-switcher"
      @cancel="handleCancel"
    >
      <template #default>
        <div class="col-12">
          <!-- Состояние загрузки -->
          <div v-if="!languageSwitcherReady" class="text-center py-4">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <div class="mt-2">{{ tg('languageSwitcher', 'loading') }}</div>
          </div>

          <!-- Состояние отображения данных -->
          <div v-else>
            <!-- Кнопка автовыбора языка -->
            <div class="mb-3">
              <button 
                class="btn w-100" 
                :class="idLanguage === null ? 'btn-primary' : 'btn-outline-secondary'"
                @click="selectLanguage(null, null)"
              >
                <i class="bi bi-arrow-clockwise me-2"></i>
                {{ tg('languageSwitcher', 'autoSelect') }}
              </button>
            </div>

            <!-- Список языков -->
            <div class="language-list">
              <div
                  v-for="language in allLanguages"
                  :key="language.idLanguage"
                  class="language-row"
                  :class="{
                    'active': language.idLanguage === idLanguage,
                    'recommended': isRecommendedLanguage(language.idLanguage),
                    'rtl': language.rtl
                  }"
                  @click="handleLanguageClick(language)"
                  style="cursor: pointer;"
              >
                <div class="language-title">
                  {{ language.selfTitle }}
                </div>
                <div class="language-flags">
                  <div
                      v-for="locale in getLocalesForLanguage(language.idLanguage)"
                      :key="locale.idLocale"
                      class="flag-item"
                      @click.stop="selectLanguage(language.idLanguage, locale.idLocale)"
                      :class="{
                          'recommended': isRecommendedLocale(language.idLanguage, locale.idLocale),
                          'selected': isSelectedLocale(language.idLanguage, locale.idLocale)
                        }"
                  >
                    <img
                        v-if="getCountryForLocale(locale.idLocale)?.code"
                        :src="'/images/country/' + getCountryForLocale(locale.idLocale)?.code + '/20x16.png'"
                        :alt="getCountryForLocale(locale.idLocale)?.code + ' flag'"
                        class="flag-image"
                    >
                    <div v-else class="international-locale">
                      <i class="bi bi-globe"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template #actions>
        <button class="btn btn-secondary" @click="closeLanguageModal">{{ tg('languageSwitcher', 'modalClose') }}</button>
      </template>
    </BaseModal>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useLanguageModal } from '@/composables/useLanguageModal';
import BaseModal from '@/composables/BaseModal.vue';
import { Language } from '@/types/Language';
import { RecommendedLanguage } from '@/store/modules/recommendedLanguages';
import { latestMessage } from '@/plugins/ws';
import {useTranslation} from "@/composables/useTranslation";

// Используем composable для управления модальным окном
const { isOpen, closeModal } = useLanguageModal();
const { tg } = useTranslation();

const store = useStore();
const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload?: any) => void;

// Переменные для управления модальным окном всех языков
const languageSwitcherReady = ref(false);
const languageSwitcherSubscribed = ref(false);

// Computed свойства
const languages = computed<Language[]>(() => store.state.languages.languages);
const recommendedLanguages = computed(() => {
  const languages = store.getters['recommendedLanguages/getUniqueLanguages'];
  return languages.sort((a: RecommendedLanguage, b: RecommendedLanguage) => a.weight < b.weight ? 1 : (a.weight > b.weight ? -1 : 0));
});

const idLanguage = computed(() => store.state.i18n.idLanguage);
const idLocale = computed(() => store.state.i18n.idLocale);

const allLanguages = computed(() =>
    languages.value
        .filter(language => language.locked)
        .sort((a, b) => a.code.localeCompare(b.code))
);

// Получаем данные из store
const locales = computed(() => store.state.locales.locales);
const countries = computed(() => store.state.countries.countries);

// Функции для работы с локалями и странами
function getLocalesForLanguage(idLanguage: number) {
  return locales.value.filter(locale => locale.idLanguage === idLanguage);
}

function getCountryForLocale(idLocale: number) {
  const locale = locales.value.find(l => l.idLocale === idLocale);
  if (!locale || !locale.idCountry) return null;
  return countries.value.find(c => c.idCountry === locale.idCountry);
}

// Функции для работы с рекомендованными языками
function isRecommendedLanguage(idLanguage: number) {
  return recommendedLanguages.value.some((lang: RecommendedLanguage) => lang.idLanguage === idLanguage);
}

function isRecommendedLocale(idLanguage: number, idLocale: number) {
  return recommendedLanguages.value.some((lang: RecommendedLanguage) =>
      lang.idLanguage === idLanguage && lang.idLocale === idLocale
  );
}

function isSelectedLocale(languageId: number, localeId: number) {
  return idLanguage.value === languageId && idLocale.value === localeId;
}

function handleLanguageClick(language: Language) {
  const locales = getLocalesForLanguage(language.idLanguage);

  // Если одна или ноль локалей - кликаем на весь язык
  if (locales.length <= 1) {
    const firstLocale = locales[0];
    selectLanguage(language.idLanguage, firstLocale?.idLocale || null);
  } else {
    // Если больше одной локали - выбираем первую рекомендованную или первую доступную
    const recommendedLocale = locales.find(locale =>
        isRecommendedLocale(language.idLanguage, locale.idLocale)
    );
    const localeToSelect = recommendedLocale || locales[0];
    selectLanguage(language.idLanguage, localeToSelect.idLocale);
  }
}

function selectLanguage(idLanguage: number | null, idLocale: number | null) {
  // Если idLanguage null - это автовыбор, передаем null значения
  if (idLanguage === null) {
    wsSend('profileUpdateLocale', {
      idLanguage: null,
      idLocale: null,
    });
    // НЕ закрываем модалку при автовыборе - пользователь может продолжить выбор
    return;
  }

  // В новой структуре данных idLocale уже приходит с языком
  // Если idLocale все еще null, найдем его в recommendedLanguages
  if (idLocale === null) {
    const recommendedLanguage = recommendedLanguages.value.find((lang: RecommendedLanguage) => lang.idLanguage === idLanguage);
    if (recommendedLanguage) {
      idLocale = recommendedLanguage.idLocale;
    }
  }

  wsSend('profileUpdateLocale', {
    idLanguage,
    idLocale,
  });
  
  // НЕ закрываем модалку при выборе языка - пользователь может выбрать несколько
}

// Lifecycle и watchers
onMounted(() => {
  // Модальное окно открывается через composable, не нужно ничего делать здесь
});

onBeforeUnmount(() => {
  if (languageSwitcherSubscribed.value) {
    wsSend('unsubscribeGroup', {
      group: 'languageSwitcher',
    });
  }
});

// Отслеживаем открытие модального окна
watch(isOpen, (newValue) => {
  if (newValue) {
    openLanguageModal();
  } else {
    // Модальное окно закрывается - ничего не делаем, так как closeLanguageModal уже вызвана
  }
});

function openLanguageModal() {
  languageSwitcherReady.value = false;
  languageSwitcherSubscribed.value = true;
  wsSend('subscribeGroup', {
    group: 'languageSwitcher',
  });
}

function closeLanguageModal() {
  if (languageSwitcherSubscribed.value) {
    wsSend('unsubscribeGroup', {
      group: 'languageSwitcher',
    });
    languageSwitcherSubscribed.value = false;
    languageSwitcherReady.value = false;
  }
  closeModal();
}

function handleCancel() {
  closeLanguageModal();
}

// Обработка WebSocket сообщений
watch(latestMessage, (data) => {
  if (!data || !data.signal) return;
  switch (data.signal) {
    case 'language-switcher-data':
      signalLanguageSwitcherData();
      break;
  }
});

function signalLanguageSwitcherData() {
  languageSwitcherReady.value = true;
}
</script>

<style scoped>
.language-list {
  max-height: 400px;
  overflow-y: auto;
}

.language-row {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  border-bottom: 1px solid #dee2e6;
  transition: background-color 0.2s ease;
}

/* Базовые состояния */
.language-row {
  background-color: transparent;
}

/* Рекомендованный язык (базовый) */
.language-row.recommended {
  background-color: rgba(255, 193, 7, 0.1);
}

/* Выбранный язык (переопределяет рекомендованный) */
.language-row.active {
  background-color: #0dcaf0 !important;
  color: white;
}

/* Hover эффекты */
.language-row:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.language-row.recommended:hover {
  background-color: rgba(255, 193, 7, 0.2);
}

.language-row.active:hover {
  background-color: #0bb5d4 !important;
}

.language-title {
  width: 25%;
  font-weight: 500;
  padding-right: 12px;
  flex-shrink: 0;
}

.language-flags {
  width: 75%;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  justify-content: flex-start;
  min-height: 20px;
}

.flag-item {
  cursor: pointer;
  transition: transform 0.1s ease;
  border-radius: 2px;
  padding: 1px;
  flex-shrink: 0;
  /* Невидимая граница по умолчанию для предотвращения "плывущих" стилей */
  border: 2px solid transparent;
}

.flag-item:hover {
  transform: scale(1.1);
  background-color: rgba(0, 123, 255, 0.2);
}

.flag-item.recommended {
  border: 2px solid #ffc107;
}

.flag-item.selected {
  border: 2px solid #000000;
}

.flag-image {
  width: 20px;
  height: 16px;
  border-radius: 2px;
  display: block;
}

.international-locale {
  width: 20px;
  height: 16px;
  border-radius: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #6c757d, #495057);
  color: white;
  font-size: 10px;
}

.international-locale i {
  font-size: 8px;
}

.language-row.rtl .language-title {
  text-align: right;
}

.language-row.rtl .language-flags {
  justify-content: flex-start;
}

/* Темная тема */
[data-bs-theme="dark"] .international-locale {
  background: linear-gradient(135deg, #495057, #343a40);
}

[data-bs-theme="dark"] .language-row {
  border-bottom-color: #495057;
}

[data-bs-theme="dark"] .language-row:hover {
  background-color: rgba(0, 123, 255, 0.2);
}

[data-bs-theme="dark"] .language-row.recommended {
  background-color: rgba(255, 193, 7, 0.15);
}

[data-bs-theme="dark"] .language-row.recommended:hover {
  background-color: rgba(255, 193, 7, 0.25);
}

[data-bs-theme="dark"] .language-row.active:hover {
  background-color: #0bb5d4;
}

[data-bs-theme="dark"] .flag-item.selected {
  border: 2px solid #ffffff;
}
</style>
